<template>
  <div id='cart-summary'>
    <div class="module-content">
      <div class="module-header">
        <h3 class="title">{{ $t(titleKey) }}</h3>
        <base-button
          :isDisabled="!cartHasItems"
          :isLoading="isEmptyBtnLoading"
          :isSecondary="true"
          :text="$t(emptyBtnKey)"
          @clicked="handleEmptyBtnClick"
        />
      </div>
      <div class="module-body">
        <div class="summary-table">
          <div v-for="(row, i) in tableRows" :key="i" class="table-row">
            <span class="label" v-html="row.labelText"></span>
            <span class="value">{{ row.value }}</span>
          </div>
        </div>
        <div class="buttons">
          <base-button :isDisabled="!cartHasItems" :isSecondary="true" :text="$t(payBtnKey)" @clicked="handleBtnClick"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BaseButton from '@/components/shared/elements/buttons/ButtonBase';

export default {
  name: 'CartSummary',
  props: {
    titleKey: {
      type: String,
      required: false,
      default: 'pages.cart.summary.title',
    },
    emptyBtnKey: {
      type: String,
      required: false,
      default: 'pages.cart.summary.buttons.empty.text',
    },
    payBtnKey: {
      type: String,
      required: false,
      default: 'pages.cart.summary.buttons.pay.text',
    },
    btnRoutePath: {
      type: String,
      required: false,
      default: '/payment/registration',
    },
  },
  components: {
    BaseButton,
  },
  data: () => ({
    isEmptyBtnLoading: false,
  }),
  created() {},
  computed: {
    ...mapGetters({
      getCurrentCart: 'cart/getCurrentCart',
    }),
    cartHasItems() {
      if (this.getCurrentCart) {
        return this.getCurrentCart.cartItems.length > 0;
      }

      return false;
    },
    tableRows() {
      const price = this.getCurrentCart?.price || {};
      const currency = this.$options.filters.toCurrency;

      const data = this.processCartItems(this.getCurrentCart);
      const rows = [];

      if (
        data.length > 0
      ) {
        data.forEach((item) => {
          rows.push({
            labelText: item.name,
            value: currency(item.totalNet || 0),
          });
          rows.push({
            labelText: `${this.$t('pages.cart.summary.rows.vat')} (${item.vatPercent ?? '-'}%)`,
            value: currency(item.totalVat || 0),
          });
        });
      }

      rows.push({
        labelText: this.$t('pages.cart.summary.rows.summaryLabel'),
        value: currency(price.grossUnitPrice || 0),
      });

      return rows;
    },
  },
  methods: {
    ...mapActions({
      clearCart: 'cart/clearCart',
    }),
    handleEmptyBtnClick() {
      this.isEmptyBtnLoading = true;
      this.clearCart(this.getCurrentCart._id).then(() => {
        this.isEmptyBtnLoading = false;
      });
    },
    handleBtnClick() {
      this.$router.push(this.btnRoutePath);
    },
    processCartItems(cartData) {
      const result = [];

      if (cartData && cartData.cartItems) {
        cartData.cartItems.forEach((day) => {
          day.cikkList.forEach((item) => {
            const amount = item.amount || 1;
            const product = item.cikk;

            const processItem = (productData, qty) => {
              const grossUnitPrice = productData.cikkar?.cikkar_bruttoegysegar || 0;
              const netUnitPrice = productData.cikkar?.cikkar_nettoegysegar || 0;
              const vatAmount = grossUnitPrice - netUnitPrice;
              const vatId = productData.cikkar?.cikkar_afakulcsid || null;
              const vatPercent = this.$VAT_PERCENT_BY_ID[vatId] ?? null;

              result.push({
                cikk_csoport_name: productData.cikkcsop_nev,
                name: productData.cikk_nev,
                quantity: qty,
                grossUnitPrice,
                netUnitPrice,
                vatAmount,
                vatPercent,
                totalGross: grossUnitPrice * qty,
                totalNet: netUnitPrice * qty,
                totalVat: vatAmount * qty,
              });
            };

            if (product.cikk_tipus === this.$ITEM_TYPES.COMBINATION && Array.isArray(product.cikk_csoport)) {
              product.cikk_csoport.forEach((subProduct) => {
                processItem(subProduct, amount);
              });
            } else {
              processItem(product, amount);
            }
          });
        });
      }

      return result;
    },
  },
};
</script>

<style lang='scss' scoped>
.module-content {
  background-color: $white;
  border-radius: $globalBorderRadius;
  box-shadow: $lightDropdownShadow;
  padding: 30px 36px 31px 33px;
}

.module-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 22px;

  @media screen and (max-width: $breakpointDownMd) {
    flex-wrap: wrap;
  }

  .title {
    text-transform: uppercase;
    font-weight: 700;
    font-size: 20px;
    margin: 0;
    line-height: 1;
  }

  // A cím és a gomb egy sorban áll. Németül, csehül, ukránul és oroszul a két
  // felirat együtt szélesebb, mint a kártya: ilyenkor a flex összenyomja őket,
  // és rés nélkül összeérnek. A bal margó gondoskodik arról, hogy a gomb soha
  // ne tapadjon a címhez; a felirat ettől több sorba kerülhet, de a gomb
  // magassága már nem fix, tehát nem vágja le a második sort.
  .button-base {
    background-color: $primaryOrange;
    box-shadow: none;
    padding: 0 10px;
    min-height: 25px;
    line-height: 1.25;
    margin: 0 0 0 12px;
    min-width: 110px;

    @media screen and (max-width: $breakpointDownMd) {
      margin-top: 10px;
      margin-left: 0;
    }

    &::v-deep {
      .loader {
        height: 25px;
      }
    }
  }
}

.table-row {
  display: flex;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 500;
  padding: 18px 0;
  position: relative;

  .label {
    padding-right: 15px;
  }

  &:after {
    content: '';
    width: 100%;
    max-width: 255px;
    height: 3px;
    background-color: $quantityInputBackground;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);

    @media screen and (max-width: $breakpointDownSm) {
      max-width: 90%;
    }
  }

  &:nth-child(2n) {
    padding: 22px 0;

    .label {
      &:before {
        content: '+';
        padding-right: 5px;
        display: inline-block;
      }
    }
  }

  &:last-child() {
    font-size: 20px;

    .value {
      font-weight: 700;
    }

    &:after {
      display: none;
    }
  }
}

.buttons {
  margin: 20px 0 0;
}
</style>
